import { useState } from 'react';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Skeleton } from '@/components/ui/skeleton';
import LedgerDialog from '@/components/admin/LedgerDialog';
import { LEDGER_FIELDS, credits, num, signedMoney } from '@/shared/ledger';
import { idOf } from '@/shared/data';
import { orderTotal } from '@/shared/pricing';
import { cn } from '@/lib/utils';

const { eq, or } = window.ApperSDK.CoreSDK;

const MAIN_KINDS = ['Activation', 'Renewal'];
const BATCH = 25;

function chunkIds(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

/** Rows of `table` whose lookup `field` points at any of `ids`. Batched, with a one-by-one fallback. */
async function fetchLinked(table, fields, field, ids, order = 'Id') {
  const out = [];
  const one = async (id) => {
    const res = await sdk.table(table).select(fields).where(eq(field, id)).orderByDesc(order).limit(200).fetch();
    if (!res?.success) throw new Error(res?.message || 'Could not load credits and cost');
    return res.data ?? [];
  };
  for (const part of chunkIds(ids, BATCH)) {
    if (part.length === 1) {
      out.push(...(await one(part[0])));
      continue;
    }
    const res = await sdk
      .table(table)
      .select(fields)
      .where(or(...part.map((id) => eq(field, id))))
      .orderByDesc(order)
      .limit(1000)
      .fetch();
    if (res?.success) out.push(...(res.data ?? []));
    else (await Promise.all(part.map(one))).forEach((list) => out.push(...list));
  }
  return out;
}

const fetchEntries = (ids) => fetchLinked('ledger_c', LEDGER_FIELDS, 'order_c', ids, 'entry_date_c');

/** Ledger totals per order: { [orderId]: { credits, cost, collected, count, typed, main, latest } }. */
export function summarizeEntries(entries, ids) {
  const wanted = new Set(ids.map(String));
  const map = {};
  entries.forEach((e) => {
    const id = idOf(e.order_c);
    if (id === null || !wanted.has(String(id))) return;
    if (!map[id]) map[id] = { credits: 0, cost: 0, collected: 0, count: 0, typed: false, main: null, latest: null };
    const s = map[id];
    const c = num(e.credits_c);
    const k = num(e.cost_c);
    const got = num(e.collected_c);
    if (c !== null || k !== null) s.typed = true;
    s.credits += c ?? 0;
    s.cost += k ?? 0;
    s.collected += got ?? 0;
    s.count += 1;
    if (!s.latest) s.latest = e;
    if (!s.main && MAIN_KINDS.includes(e.kind_c)) s.main = e;
  });
  return map;
}

/** Credits used and cost for a set of orders, read from the ledger. */
export function useOrderCosts(ids) {
  const list = (ids ?? []).filter((id) => id !== null && id !== undefined);
  const key = list.join(',');
  return useFetch(async () => {
    if (!list.length) return {};
    return summarizeEntries(await fetchEntries(list), list);
  }, [key]);
}

/**
 * Credits, cost and collected per customer, across all their orders:
 * { [customerId]: { credits, cost, collected, typed, orders } }.
 */
export function useCustomerCosts(customerIds) {
  const list = (customerIds ?? []).filter((id) => id !== null && id !== undefined);
  const key = list.join(',');
  return useFetch(async () => {
    if (!list.length) return {};
    const orders = await fetchLinked('orders_c', ['Name', 'customer_c'], 'customer_c', list);
    const owner = {};
    orders.forEach((o) => {
      const c = idOf(o.customer_c);
      if (c !== null) owner[o.Id] = c;
    });
    const byOrder = summarizeEntries(await fetchEntries(Object.keys(owner).map(Number)), Object.keys(owner));
    const out = {};
    Object.entries(owner).forEach(([orderId, customerId]) => {
      const s = byOrder[orderId];
      if (!out[customerId]) out[customerId] = { credits: 0, cost: 0, collected: 0, typed: false, orders: 0 };
      const t = out[customerId];
      t.orders += 1;
      if (!s) return;
      t.credits += s.credits;
      t.cost += s.cost;
      t.collected += s.collected;
      t.typed = t.typed || s.typed;
    });
    return out;
  }, [key]);
}

/** Totals across every order in a summary map. */
export function totalCosts(map) {
  return Object.values(map ?? {}).reduce(
    (t, s) => ({
      credits: t.credits + s.credits,
      cost: t.cost + s.cost,
      collected: t.collected + s.collected,
      typed: t.typed || s.typed,
    }),
    { credits: 0, cost: 0, collected: 0, typed: false },
  );
}

/**
 * Edit an order's credits used and cost in place.
 * Opens the order's New line or Renewal ledger entry, or starts one when it has none.
 */
export function useCostEditor(onSaved) {
  const [state, setState] = useState({ open: false, order: null, entry: null, count: 0 });

  function edit(order, summary) {
    setState({ open: true, order, entry: summary?.main ?? summary?.latest ?? null, count: summary?.count ?? 0 });
  }

  const description =
    state.count > 1
      ? `This order has ${state.count} ledger entries. You are editing the latest New line or Renewal; the order page lists them all.`
      : 'For your books only. Customers never see this.';

  const dialog = (
    <LedgerDialog
      open={state.open}
      onOpenChange={(open) => setState((s) => ({ ...s, open }))}
      order={state.order}
      entry={state.entry}
      onSaved={onSaved}
      title={state.order ? `Credits and cost · ${state.order.Name}` : 'Credits and cost'}
      description={description}
    />
  );

  return [edit, dialog];
}

/**
 * Profit for one order: what was collected (from the ledger, or the order total when nothing
 * was logged as collected) minus the cost. Null until a cost or credits figure is typed in.
 */
export function orderProfit(order, summary) {
  if (!summary?.typed) return null;
  const logged = Number(summary.collected) || 0;
  const collected = logged || Number(orderTotal(order)) || 0;
  const cost = Number(summary.cost) || 0;
  const profit = collected - cost;
  return { profit, collected, cost, margin: collected > 0 ? Math.round((profit / collected) * 100) : null };
}

/** Table cell: profit or loss for the order, in green or red. */
export function ProfitCell({ order, summary, loading }) {
  if (loading && !summary) return <Skeleton className="ml-auto h-8 w-16" />;
  const p = orderProfit(order, summary);
  if (!p) {
    return (
      <span className="text-xs text-muted-foreground" title="Add credits or cost to see the profit">
        —
      </span>
    );
  }
  const up = p.profit >= 0;
  return (
    <span className="tabular-nums" title={`${signedMoney(p.collected)} collected − ${signedMoney(p.cost)} cost`}>
      <span className={cn('block text-sm font-semibold', up ? 'text-success' : 'text-destructive')}>
        {signedMoney(p.profit)}
      </span>
      <span className="block text-xs text-muted-foreground">
        {p.margin === null ? (up ? 'profit' : 'loss') : `${p.margin}% ${up ? 'margin' : 'loss'}`}
      </span>
    </span>
  );
}

/** Table cell: credits used and cost, click to edit. */
export function CostCell({ order, summary, loading, onEdit, className }) {
  if (loading && !summary) return <Skeleton className="h-8 w-20" />;
  const has = !!summary?.typed;
  return (
    <button
      type="button"
      onClick={() => onEdit(order, summary)}
      aria-label={`Edit credits and cost for ${order.Name}`}
      className={cn(
        'group inline-flex items-center gap-2 rounded-md px-2 py-1 text-left transition-colors duration-(--transition-fast) hover:bg-accent hover:text-accent-foreground',
        className,
      )}
    >
      {has ? (
        <span className="tabular-nums">
          <span className="block text-sm">{credits(summary.credits)} cr</span>
          <span className="block text-xs text-muted-foreground">{signedMoney(summary.cost)} cost</span>
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">Add credits</span>
      )}
      <ApperIcon name="Pencil" size={14} className="shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100" />
    </button>
  );
}
